import { cn } from '@/lib/utils';

/**
 * Spinner — pure CSS rotating arc. No SVG, no animation library.
 *
 * Replaces Mantine's `<Loader />`. CF's loading affordances are
 * minimal — a tiny gray arc that spins. Same here. The arc is a
 * `border: 2px solid transparent` with one side colored, on a
 * round element. Rotates via `animate-spin` (Tailwind built-in).
 */
interface SpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  // Tint the rotating arc. Defaults to neutral; pass 'primary'
  // for orange (e.g. inside a CTA button while it's pending).
  tone?: 'neutral' | 'primary';
  className?: string;
}

const SIZE_PX: Record<NonNullable<SpinnerProps['size']>, number> = {
  xs: 10,
  sm: 14,
  md: 18,
  lg: 24,
};

export function Spinner({ size = 'sm', tone = 'neutral', className }: SpinnerProps) {
  const px = SIZE_PX[size];
  const arcColor = tone === 'primary' ? 'border-t-theia-500' : 'border-t-neutral-400';
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-transparent',
        arcColor,
        className,
      )}
      style={{ width: px, height: px }}
    />
  );
}
